import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

// Green/Blue colorful wispy cloud
const ColorfulCloud = ({ size, top, left, delay, color = 'white', opacity = 0.4 }) => {
  const colorMap = {
    white: 'rgba(255,255,255',
    green: 'rgba(74,222,128',
    blue: 'rgba(96,165,250',
    teal: 'rgba(45,212,191',
  };
  const baseColor = colorMap[color] || colorMap.white;
  
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        width: size,
        height: size * 0.4,
        top: top,
        left: left,
      }}
      animate={{ 
        x: [0, 40, 0],
        y: [0, -10, 0],
        scale: [1, 1.05, 1],
      }}
      transition={{ 
        duration: 20 + delay * 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: delay,
      }}
    >
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: `linear-gradient(135deg, ${baseColor},${opacity}) 0%, ${baseColor},${opacity * 0.3}) 100%)`,
          filter: 'blur(20px)',
        }}
      />
      <div 
        className="absolute rounded-full"
        style={{
          width: '60%',
          height: '70%',
          top: '-20%',
          left: '20%',
          background: `radial-gradient(ellipse, ${baseColor},${opacity * 0.8}) 0%, transparent 70%)`,
          filter: 'blur(15px)',
        }}
      />
      <div 
        className="absolute rounded-full"
        style={{
          width: '40%',
          height: '50%',
          top: '-10%',
          right: '10%',
          background: `radial-gradient(ellipse, ${baseColor},${opacity * 0.6}) 0%, transparent 70%)`,
          filter: 'blur(12px)',
        }}
      />
    </motion.div>
  );
};

// Wispy cloud - scattered throughout for immersive feel
const WispyCloud = ({ size, top, left, delay, theme, opacity = 0.3 }) => {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        width: size,
        height: size * 0.4,
        top: top,
        left: left,
      }}
      animate={{ 
        x: [0, 40, 0],
        y: [0, -10, 0],
        scale: [1, 1.05, 1],
      }}
      transition={{ 
        duration: 20 + delay * 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: delay,
      }}
    >
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: theme === 'sunrise' 
            ? `linear-gradient(135deg, rgba(255,255,255,${opacity}) 0%, rgba(255,255,255,${opacity * 0.3}) 100%)`
            : `linear-gradient(135deg, rgba(148,163,184,${opacity}) 0%, rgba(45,212,191,${opacity * 0.4}) 100%)`,
          filter: 'blur(20px)',
        }}
      />
      <div 
        className="absolute rounded-full"
        style={{
          width: '60%',
          height: '70%',
          top: '-20%',
          left: '20%',
          background: theme === 'sunrise' 
            ? `radial-gradient(ellipse, rgba(255,255,255,${opacity * 0.8}) 0%, transparent 70%)`
            : `radial-gradient(ellipse, rgba(255,255,255,${opacity * 0.5}) 0%, transparent 70%)`,
          filter: 'blur(15px)',
        }}
      />
      <div 
        className="absolute rounded-full"
        style={{
          width: '40%',
          height: '50%',
          top: '-10%',
          right: '10%',
          background: theme === 'sunrise' 
            ? `radial-gradient(ellipse, rgba(255,255,255,${opacity * 0.6}) 0%, transparent 70%)`
            : `radial-gradient(ellipse, rgba(255,255,255,${opacity * 0.4}) 0%, transparent 70%)`,
          filter: 'blur(12px)',
        }}
      />
    </motion.div>
  );
};

// Small floating wisp for depth
const FloatingWisp = ({ top, left, delay, theme }) => {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{ top, left }}
      animate={{ 
        x: [0, 30, 0],
        y: [0, -20, 0],
        opacity: [0.2, 0.5, 0.2],
      }}
      transition={{ 
        duration: 15 + delay * 3,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: delay,
      }}
    >
      <div 
        className="w-20 h-10 rounded-full"
        style={{
          background: theme === 'sunrise' 
            ? 'radial-gradient(ellipse, rgba(255,255,255,0.4) 0%, transparent 70%)'
            : 'radial-gradient(ellipse, rgba(255,255,255,0.25) 0%, transparent 70%)',
          filter: 'blur(10px)',
        }}
      />
    </motion.div>
  );
};

// Twinkling star for Rolls Royce starlight effect
const Star = ({ top, left, size, delay }) => {
  return (
    <motion.div
      className="absolute rounded-full bg-white pointer-events-none"
      style={{
        top: `${top}%`,
        left: `${left}%`,
        width: size,
        height: size,
      }}
      animate={{ 
        opacity: [0.2, 1, 0.2],
        scale: [0.8, 1.2, 0.8],
      }}
      transition={{ 
        duration: 2 + Math.random() * 2,
        repeat: Infinity,
        delay: delay,
        ease: 'easeInOut',
      }}
    />
  );
};

// Data visualization - Line chart (more visible)
const LineChart = ({ style, theme, delay }) => {
  return (
    <motion.svg
      width="200"
      height="100"
      viewBox="0 0 200 100"
      className="absolute pointer-events-none"
      style={style}
      initial={{ opacity: 0, pathLength: 0 }}
      animate={{ opacity: theme === 'sunrise' ? 0.3 : 0.35, pathLength: 1 }}
      transition={{ duration: 2, delay }}
    >
      <motion.g stroke={theme === 'sunrise' ? 'rgba(255,255,255,0.15)' : 'rgba(74,222,128,0.15)'} strokeWidth="0.5">
        {[0, 25, 50, 75, 100].map((y) => (
          <line key={y} x1="0" y1={y} x2="200" y2={y} />
        ))}
      </motion.g>
      <motion.path
        d="M0 80 L30 60 L60 70 L90 30 L120 45 L150 20 L180 35 L200 15"
        fill="none"
        stroke={theme === 'sunrise' ? 'rgba(255,255,255,0.5)' : 'rgba(74,222,128,0.6)'}
        strokeWidth="2.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 3, delay, repeat: Infinity, repeatDelay: 5 }}
      />
      {[[30, 60], [90, 30], [150, 20]].map(([cx, cy], i) => (
        <motion.circle
          key={i}
          cx={cx}
          cy={cy}
          r="5"
          fill={theme === 'sunrise' ? 'rgba(255,255,255,0.6)' : 'rgba(74,222,128,0.7)'}
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          transition={{ duration: 0.5, delay: delay + 1 + i * 0.3 }}
        />
      ))}
    </motion.svg>
  );
};

// Bar chart (more visible)
const BarChart = ({ style, theme, delay }) => {
  const bars = [60, 80, 45, 90, 70, 55];
  return (
    <motion.svg
      width="150"
      height="100"
      viewBox="0 0 150 100"
      className="absolute pointer-events-none"
      style={style}
      initial={{ opacity: 0 }}
      animate={{ opacity: theme === 'sunrise' ? 0.25 : 0.3 }}
      transition={{ duration: 1, delay }}
    >
      {bars.map((height, i) => (
        <motion.rect
          key={i}
          x={i * 25 + 5}
          y={100 - height}
          width="15"
          height={height}
          rx="2"
          fill={theme === 'sunrise' 
            ? `rgba(255,255,255,${0.25 + i * 0.08})` 
            : `rgba(74,222,128,${0.25 + i * 0.08})`}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.8, delay: delay + i * 0.1 }}
          style={{ transformOrigin: 'bottom' }}
        />
      ))}
    </motion.svg>
  );
};

// Pie chart (more visible)
const PieChart = ({ style, theme, delay }) => {
  return (
    <motion.svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      className="absolute pointer-events-none"
      style={style}
      initial={{ opacity: 0, rotate: -90 }}
      animate={{ opacity: theme === 'sunrise' ? 0.25 : 0.3, rotate: 0 }}
      transition={{ duration: 2, delay }}
    >
      <circle
        cx="50"
        cy="50"
        r="40"
        fill="none"
        stroke={theme === 'sunrise' ? 'rgba(255,255,255,0.2)' : 'rgba(74,222,128,0.2)'}
        strokeWidth="20"
      />
      <motion.circle
        cx="50"
        cy="50"
        r="40"
        fill="none"
        stroke={theme === 'sunrise' ? 'rgba(255,255,255,0.5)' : 'rgba(74,222,128,0.55)'}
        strokeWidth="20"
        strokeDasharray="188 63"
        initial={{ strokeDashoffset: 251 }}
        animate={{ strokeDashoffset: 0 }}
        transition={{ duration: 2, delay }}
      />
    </motion.svg>
  );
};

// Floating metric (darker, more subtle)
const FloatingMetric = ({ value, label, style, theme, delay }) => {
  return (
    <motion.div
      className="absolute pointer-events-none text-center"
      style={style}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: theme === 'sunrise' ? 0.12 : 0.2, y: 0 }}
      transition={{ duration: 1, delay }}
    >
      <div className={`text-3xl font-bold ${
        theme === 'sunrise' ? 'text-blue-900/25' : 'text-emerald-400/30'
      }`}>
        {value}
      </div>
      <div className={`text-xs ${
        theme === 'sunrise' ? 'text-blue-900/20' : 'text-teal-300/25'
      }`}>
        {label}
      </div>
    </motion.div>
  );
};

// Money rain for brainstorm
const MoneyRain = ({ index }) => {
  const startX = (index * 4) % 100;
  const isBill = index % 3 === 0;
  
  return (
    <motion.div
      className="absolute text-2xl pointer-events-none font-bold"
      style={{
        left: `${startX}%`,
        color: 'rgba(34, 197, 94, 0.8)',
        textShadow: '0 0 10px rgba(34, 197, 94, 0.5)',
      }}
      initial={{ y: '-10vh', opacity: 0, rotate: -15 }}
      animate={{ 
        y: '110vh',
        opacity: [0, 1, 1, 0],
        rotate: [-15, 15, -15],
      }}
      transition={{ 
        duration: 3 + (index % 3),
        repeat: Infinity,
        delay: index * 0.2,
        ease: 'linear',
      }}
    >
      {isBill ? '💵' : '$'}
    </motion.div>
  );
};

// Lightning bolt component for brainstorm
const LightningBolt = ({ left, delay }) => {
  return (
    <motion.svg
      className="absolute pointer-events-none"
      style={{ left: `${left}%`, top: '0%' }}
      width="60"
      height="200"
      viewBox="0 0 60 200"
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ 
        opacity: [0, 0, 1, 1, 0, 0],
        scaleY: [0, 0, 1, 1, 1, 0],
      }}
      transition={{ 
        duration: 0.8,
        repeat: Infinity,
        repeatDelay: delay,
        times: [0, 0.1, 0.15, 0.3, 0.35, 1],
      }}
    >
      <motion.path
        d="M35 0 L20 70 L35 70 L15 130 L40 130 L10 200 L45 110 L25 110 L45 50 L30 50 Z"
        fill="rgba(250, 204, 21, 0.9)"
        filter="url(#lightning-glow)"
      />
      <defs>
        <filter id="lightning-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </motion.svg>
  );
};

// Small spark lightning
const SparkLightning = ({ top, left, delay }) => {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{ top: `${top}%`, left: `${left}%` }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0, 0, 1, 0.5, 0, 0],
        scale: [0, 0, 1.2, 1, 0.8, 0],
      }}
      transition={{ 
        duration: 0.5,
        repeat: Infinity,
        repeatDelay: delay,
        times: [0, 0.2, 0.3, 0.5, 0.7, 1],
      }}
    >
      <svg width="40" height="80" viewBox="0 0 40 80">
        <path
          d="M25 0 L15 30 L25 30 L10 55 L30 25 L20 25 L30 0 Z"
          fill="rgba(250, 204, 21, 0.85)"
          style={{ filter: 'drop-shadow(0 0 8px rgba(250, 204, 21, 0.8))' }}
        />
      </svg>
    </motion.div>
  );
};

// Storm cloud for brainstorm mode - white/grey wispy clouds
const StormCloud = ({ size, top, left, delay }) => {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        width: size,
        height: size * 0.5,
        top: top,
        left: left,
      }}
      animate={{ 
        x: [0, 30, 0],
        scale: [1, 1.05, 1],
      }}
      transition={{ 
        duration: 12 + delay * 2,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {/* Main cloud body - white/grey wispy */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(200,200,200,0.12) 50%, rgba(150,150,150,0.06) 100%)',
          filter: 'blur(25px)',
        }}
      />
      {/* Additional wisp layer */}
      <div
        className="absolute rounded-full"
        style={{
          width: '70%',
          height: '60%',
          top: '-15%',
          left: '15%',
          background: 'radial-gradient(ellipse, rgba(255,255,255,0.18) 0%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />
    </motion.div>
  );
};

// Generate random stars for Rolls Royce starlight effect
const generateStars = (count) => {
  const stars = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 3,
    });
  }
  return stars;
};

const STARS = generateStars(150);

export const ImmersiveBackground = () => {
  const { theme } = useTheme();

  return (
    <div className="fixed inset-0 overflow-hidden -z-10">
      {/* Main gradient - Bright blue sky with vivid pink/orange/yellow sunrise streaks */}
      <AnimatePresence mode="wait">
        <motion.div
          key={theme}
          className="absolute inset-0"
          style={{
            background: theme === 'sunrise'
              ? 'linear-gradient(180deg, #0284c7 0%, #0ea5e9 15%, #38bdf8 30%, #7dd3fc 45%, #bae6fd 58%, #fef3c7 72%, #fde68a 80%, #fdba74 88%, #fb923c 94%, #f472b6 100%)'
              : 'linear-gradient(180deg, #0a0a0a 0%, #0f172a 20%, #1e293b 40%, #1e3a2f 60%, #1e293b 80%, #0f172a 100%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        />
      </AnimatePresence>

      {/* Starlight ceiling effect for brainstorm (Rolls Royce style) */}
      <AnimatePresence>
        {theme === 'brainstorm' && (
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            {STARS.map((star) => (
              <Star
                key={star.id}
                top={star.top}
                left={star.left}
                size={star.size}
                delay={star.delay}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Readability overlay - darker for better text visibility */}
      <div 
        className="absolute inset-0"
        style={{
          background: theme === 'sunrise'
            ? 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.15) 100%)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.4) 100%)',
        }}
      />

      {/* WISPY CLOUDS - white only for sunrise, stormy for brainstorm */}
      {theme === 'sunrise' ? (
        <>
          {/* White wispy clouds only */}
          <WispyCloud size={500} top="2%" left="-5%" delay={0} theme={theme} opacity={0.5} />
          <WispyCloud size={400} top="8%" left="70%" delay={1.5} theme={theme} opacity={0.45} />
          <WispyCloud size={350} top="15%" left="30%" delay={0.8} theme={theme} opacity={0.4} />
          <WispyCloud size={450} top="35%" left="-8%" delay={2} theme={theme} opacity={0.45} />
          <WispyCloud size={380} top="40%" left="55%" delay={1} theme={theme} opacity={0.5} />
          <WispyCloud size={420} top="48%" left="20%" delay={2.5} theme={theme} opacity={0.4} />
          <WispyCloud size={500} top="62%" left="65%" delay={0.5} theme={theme} opacity={0.5} />
          <WispyCloud size={400} top="70%" left="5%" delay={1.8} theme={theme} opacity={0.45} />
          <WispyCloud size={350} top="80%" left="45%" delay={2.2} theme={theme} opacity={0.4} />
          
          <FloatingWisp top="12%" left="50%" delay={0} theme={theme} />
          <FloatingWisp top="25%" left="10%" delay={1} theme={theme} />
          <FloatingWisp top="55%" left="85%" delay={0.5} theme={theme} />
          <FloatingWisp top="75%" left="25%" delay={1.5} theme={theme} />
          <FloatingWisp top="88%" left="70%" delay={2} theme={theme} />
        </>
      ) : (
        <>
          {/* Storm clouds for brainstorm - scattered across full screen */}
          <StormCloud size={400} top="0%" left="0%" delay={0} />
          <StormCloud size={350} top="5%" left="60%" delay={0.5} />
          <StormCloud size={300} top="25%" left="80%" delay={1} />
          <StormCloud size={380} top="40%" left="5%" delay={0.3} />
          <StormCloud size={320} top="60%" left="70%" delay={1.5} />
          <StormCloud size={360} top="75%" left="25%" delay={0.8} />
        </>
      )}

      {/* Data visualizations */}
      <LineChart style={{ top: '15%', left: '5%' }} theme={theme} delay={0.5} />
      <BarChart style={{ top: '60%', right: '10%' }} theme={theme} delay={1} />
      <PieChart style={{ top: '30%', right: '15%' }} theme={theme} delay={1.5} />
      <LineChart style={{ bottom: '20%', left: '15%' }} theme={theme} delay={2} />

      {/* Money rain for brainstorm */}
      <AnimatePresence>
        {theme === 'brainstorm' && (
          <>
            {[...Array(20)].map((_, i) => (
              <MoneyRain key={`money-${i}`} index={i} />
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Lightning bolts for brainstorm - sporadic */}
      <AnimatePresence>
        {theme === 'brainstorm' && (
          <>
            {/* Large lightning bolts */}
            <LightningBolt left={15} delay={4} />
            <LightningBolt left={75} delay={6} />
            <LightningBolt left={45} delay={8} />
            <LightningBolt left={85} delay={5} />
            
            {/* Small spark lightning scattered */}
            <SparkLightning top={20} left={25} delay={3} />
            <SparkLightning top={40} left={70} delay={5} />
            <SparkLightning top={60} left={15} delay={4} />
            <SparkLightning top={30} left={55} delay={7} />
            <SparkLightning top={70} left={80} delay={6} />
            <SparkLightning top={15} left={90} delay={8} />
            <SparkLightning top={50} left={35} delay={4.5} />
            <SparkLightning top={80} left={60} delay={5.5} />
          </>
        )}
      </AnimatePresence>

      {/* Aurora glow effect for brainstorm */}
      {theme === 'brainstorm' && (
        <>
          <motion.div 
            className="absolute pointer-events-none"
            style={{
              top: '10%',
              left: '5%',
              width: '40%',
              height: '30%',
              background: 'radial-gradient(ellipse, rgba(74,222,128,0.15) 0%, rgba(34,197,94,0.08) 40%, transparent 70%)',
              filter: 'blur(40px)',
            }}
            animate={{ 
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div 
            className="absolute pointer-events-none"
            style={{
              top: '30%',
              right: '10%',
              width: '35%',
              height: '25%',
              background: 'radial-gradient(ellipse, rgba(45,212,191,0.12) 0%, rgba(74,222,128,0.06) 40%, transparent 70%)',
              filter: 'blur(35px)',
            }}
            animate={{ 
              opacity: [0.4, 0.7, 0.4],
              scale: [1, 1.15, 1],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
        </>
      )}
    </div>
  );
};

export default ImmersiveBackground;
